import type { ReactNode } from 'react'
import { Pressable, StyleSheet } from 'react-native'

import { DEPOSIT_METRICS } from '../../screens/depositMetrics'

import { Box, Text, useTheme } from '../../theme/restyle'

export interface AccountBalanceChipProps {
  /** What the balance is, as the host names it (e.g. `Total cash`). */
  label: string
  /** The formatted balance (e.g. `$366,715.12`). */
  value: string
  /** A 32px avatar or app glyph. The SDK never fetches one itself. */
  avatar?: ReactNode
  /** Links back out to the host context (e.g. the profile page). */
  onPress?: () => void
  accessibilityLabel?: string
}

const AVATAR_SIZE = DEPOSIT_METRICS.balance.avatarSize

/**
 * Top-level account context in the deposit header: a label + balance pair with
 * the host's avatar beside them. Everything shown is host-supplied — this is
 * the integrator's app talking, not funkit data.
 */
export function AccountBalanceChip({
  label,
  value,
  avatar,
  onPress,
  accessibilityLabel,
}: AccountBalanceChipProps) {
  const theme = useTheme()
  // The cents read as a suffix: dimmer and smaller than the whole dollars.
  const [whole, cents] = splitCents(value)

  const content = (
    <Box flexDirection="row" alignItems="center" style={styles.row}>
      <Box alignItems="flex-end" style={styles.balance}>
        <Text
          numberOfLines={1}
          style={[styles.label, { color: theme.content.elements.secondary }]}
        >
          {label}
        </Text>
        <Text weight="medium" numberOfLines={1} style={styles.value}>
          {whole}
          {cents ? (
            <Text
              weight="medium"
              style={[
                styles.cents,
                { color: theme.content.elements.secondary },
              ]}
            >
              {cents}
            </Text>
          ) : null}
        </Text>
      </Box>
      <Box style={styles.avatar}>{avatar}</Box>
    </Box>
  )

  if (!onPress) {
    return content
  }
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? `${label} ${value}`}
      hitSlop={8}
    >
      {content}
    </Pressable>
  )
}

/** `$366,715.12` → `['$366,715', '.12']`; no decimal point → no suffix. */
function splitCents(value: string): [string, string] {
  const point = value.lastIndexOf('.')
  if (point < 0) {
    return [value, '']
  }
  return [value.slice(0, point), value.slice(point)]
}

const styles = StyleSheet.create({
  row: { gap: DEPOSIT_METRICS.balance.avatarGap },
  balance: {
    height: DEPOSIT_METRICS.header.height,
    paddingTop: DEPOSIT_METRICS.balance.labelTop - DEPOSIT_METRICS.header.top,
  },
  label: {
    fontSize: DEPOSIT_METRICS.balance.labelSize,
    lineHeight: DEPOSIT_METRICS.balance.labelLineHeight,
    letterSpacing: DEPOSIT_METRICS.balance.labelTracking,
  },
  value: {
    fontSize: DEPOSIT_METRICS.balance.valueSize,
    lineHeight: DEPOSIT_METRICS.balance.valueLineHeight,
    letterSpacing: DEPOSIT_METRICS.balance.valueTracking,
    marginTop:
      DEPOSIT_METRICS.balance.valueTop -
      DEPOSIT_METRICS.balance.labelTop -
      DEPOSIT_METRICS.balance.labelLineHeight,
  },
  cents: {
    fontSize: DEPOSIT_METRICS.balance.centsSize,
    lineHeight: DEPOSIT_METRICS.balance.valueLineHeight,
  },
  avatar: {
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    borderRadius: 999,
    overflow: 'hidden',
  },
})
